import AppDescription from '@nxsys/components/AppDescription';
import AppLoader from '@nxsys/components/AppLoader';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { paymentSettingActions } from '@nxsys/store/slices/paymentSetting/reducer/paymentSettingSlice';
import {
  Deduction,
  DeductionParams,
} from '@nxsys/store/slices/paymentSetting/types';
import { Icons } from '@nxsys/types/icons';
import { Descriptions, Tag } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeductionForm, {
  DeductionFormRef,
  DeductionFormValues,
} from '../components/DeductionForm';

type ViewDeductionProps = {
  isOpen: boolean;
  toggle: () => void;
  deductionId: string | undefined;
  refetchData: () => void;
};

const ViewDeductionModal: React.FC<ViewDeductionProps> = ({
  isOpen,
  toggle,
  deductionId,
  refetchData,
}: ViewDeductionProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const dispatch = useDispatch<AppDispatch>();

  const formRef = useRef<DeductionFormRef>(null);

  const [data, setData] = useState<Deduction | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getDetail = async (id: string) => {
    setIsLoadingData(true);
    const res = await dispatch(
      paymentSettingActions.getDeductionById({ id }),
    ).unwrap();
    setData(res.data);
    setIsLoadingData(false);
  };

  useEffect(() => {
    if (deductionId && isOpen) getDetail(deductionId);
  }, [isOpen, deductionId]);

  const handleSubmit = (values: DeductionFormValues) => {
    if (!data?.id) return;

    setIsSubmitting(true);
    let params: DeductionParams;
    // NX-5228: country decides the payload shape, so it is tested first — the
    // matching create path already does. Checking `!isBureau` first sent an
    // umbrella-Ireland edit the UK-shaped beforeTax/beforeNI/beforePension set
    // and dropped every PRSI/USC/ASC/LPT flag.
    if (isIreland) {
      params = {
        description: values.description ?? '',
        taxCanBeDeducted: Boolean(values.taxCanBeDeducted),
        employeePRSICanBeDeducted: Boolean(values.employeePRSICanBeDeducted),
        employerPRSICanBeDeducted: Boolean(values.employerPRSICanBeDeducted),
        uscCanBeDeducted: Boolean(values.uscCanBeDeducted),
        ascCanBeDeducted: Boolean(values.ascCanBeDeducted),
        pensionSchemaDeductionCanBeDeducted: Boolean(
          values.pensionSchemaDeductionCanBeDeducted,
        ),
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        lptCanBeDeducted: Boolean(values.lptCanBeDeducted),
      };
    } else if (!isBureau) {
      params = {
        description: values.description ?? '',
        includeInCisCalculation: Boolean(values.includeInCisCalculation),
        beforeTax: Boolean(values.beforeTax),
        beforeNI: Boolean(values.beforeNI),
        beforePension: Boolean(values.beforePension),
      };
    } else {
      params = {
        description: values.description ?? '',
        beforeTax: Boolean(values.beforeTax),
        beforeNI: Boolean(values.beforeNI),
        beforePension: Boolean(values.beforePension),
      };
    }
    dispatch(
      paymentSettingActions.updateDeductionById({
        id: data.id,
        payload: params,
      }),
    )
      .then((res) => {
        refetchData();
        setData((res.payload as any)?.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const descriptionItems = [
    {
      label: messages['paymentSetting.deduction.deductionCode'],
      value: data?.deductionCode ?? '',
    },
    {
      label: messages['paymentSetting.payment.description'],
      value: data?.description ?? '',
    },
    ...(!isIreland
      ? [
          ...(!isBureau
            ? [
                {
                  label: messages['paymentSetting.payment.beforeCisTax'],
                  value: (
                    <Tag>
                      {data?.includeInCisCalculation
                        ? messages['common.yes']
                        : messages['common.no']}
                    </Tag>
                  ),
                },
              ]
            : []),
          {
            label: messages['paymentSetting.payment.beforeTax'],
            value: (
              <Tag>
                {data?.beforeTax
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeNI'],
            value: (
              <Tag>
                {data?.beforeNI
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforePension'],
            value: (
              <Tag>
                {data?.beforePension
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : [
          {
            label: messages['paymentSetting.payment.beforeTax'],
            value: (
              <Tag>
                {data?.taxCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeEmployeePRSI'],
            value: (
              <Tag>
                {data?.employeePRSICanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeEmployerPRSI'],
            value: (
              <Tag>
                {data?.employerPRSICanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeUSC'],
            value: (
              <Tag>
                {data?.uscCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeASC'],
            value: (
              <Tag>
                {data?.ascCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label:
              messages['paymentSetting.payment.beforePensionSchemeDeductions'],
            value: (
              <Tag>
                {data?.pensionSchemaDeductionCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeLPT'],
            value: (
              <Tag>
                {data?.lptCanBeDeducted
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
          {
            label: messages['paymentSetting.payment.beforeRCT'],
            value: (
              <Tag>
                {data?.includeInCisCalculation
                  ? messages['common.yes']
                  : messages['common.no']}
              </Tag>
            ),
          },
        ]),
  ];

  return (
    <AppModalContainer
      title={messages['paymentSetting.deduction.deductionDetail']}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        isEditing
          ? [
              {
                name: messages['common.cancel'],
                variant: 'outlined',
                onClick: () => setIsEditing(false),
              },
              {
                name: messages['common.save'],
                buttonType: 'primary',
                onClick: () => formRef.current?.submit(),
                loading: isSubmitting,
                disabled: isSubmitting,
              },
            ]
          : [
              {
                name: messages['common.edit'],
                buttonType: 'primary',
                leftIcon: (
                  <Icons.IconEdit color={defaultTheme.theme.palette.white} />
                ),
                onClick: () => setIsEditing(true),
              },
            ]
      }
    >
      {(isLoadingData || isSubmitting) && <AppLoader />}
      {data && (
        <>
          {!isEditing ? (
            <AppDescription
              column={1}
              labelStyle={{ width: 216 }}
              bordered={false}
              colon={false}
              size="middle"
            >
              {descriptionItems?.map((item, _index) => (
                <Descriptions.Item key={_index} label={item.label} span={1}>
                  {item.value ?? '-'}
                </Descriptions.Item>
              ))}
            </AppDescription>
          ) : (
            <DeductionForm
              formRef={formRef}
              onSubmit={handleSubmit}
              defaultValues={{
                deductionCode: data?.deductionCode,
                description: data?.description,
                ...(!isBureau || isIreland
                  ? {
                      includeInCisCalculation: data?.includeInCisCalculation,
                    }
                  : {}),
                ...(!isIreland
                  ? {
                      beforeTax: data?.beforeTax,
                      beforeNI: data?.beforeNI,
                      beforePension: data?.beforePension,
                    }
                  : {
                      taxCanBeDeducted: data?.taxCanBeDeducted,
                      employeePRSICanBeDeducted:
                        data?.employeePRSICanBeDeducted,
                      employerPRSICanBeDeducted:
                        data?.employerPRSICanBeDeducted,
                      uscCanBeDeducted: data?.uscCanBeDeducted,
                      ascCanBeDeducted: data?.ascCanBeDeducted,
                      pensionSchemaDeductionCanBeDeducted:
                        data?.pensionSchemaDeductionCanBeDeducted,
                      lptCanBeDeducted: data?.lptCanBeDeducted,
                    }),
              }}
            />
          )}
        </>
      )}
    </AppModalContainer>
  );
};

export default ViewDeductionModal;
